import { useEffect, useRef } from 'react';
import './AquaJetAnimation.css';

export default function AquaJetAnimation({ playerRef, opponentRef, onComplete }) {
  const animRef = useRef(null);

  useEffect(() => {
    const attackerEl = playerRef.current;
    const defenderEl = opponentRef.current;
    const container = defenderEl.closest(".battle-container");
    
    if (!attackerEl || !defenderEl || !container) {
      onComplete();
      return;
    }

    // Get positions for both Pokemon
    const attackerRect = attackerEl.getBoundingClientRect();
    const defenderRect = defenderEl.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    // Calculate centers
    const attackerCenterX = attackerRect.left - containerRect.left + attackerRect.width / 2;
    const attackerCenterY = attackerRect.top - containerRect.top + attackerRect.height / 2;
    const defenderCenterX = defenderRect.left - containerRect.left + defenderRect.width / 2;
    const defenderCenterY = defenderRect.top - containerRect.top + defenderRect.height / 2;

    // Calculate direction and distance for dash
    const dx = defenderCenterX - attackerCenterX;
    const dy = defenderCenterY - attackerCenterY;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const angle = Math.atan2(dy, dx);

    // Track all created elements for cleanup
    const elements = [];
    const timers = [];
    
    // PHASE 1: WATER AURA PREPARATION (0-200ms)
    // Initial attacker animation - water aura buildup
    attackerEl.style.transition = 'transform 0.2s ease-in-out, filter 0.2s ease-in-out';
    attackerEl.style.transform = 'scale(1.05)';
    attackerEl.style.filter = 'brightness(1.2) drop-shadow(0 0 15px #00BFFF)';
    
    // Create water aura around attacker
    const waterAura = document.createElement('div');
    waterAura.className = 'aqua-jet-water-aura';
    waterAura.style.left = `${attackerCenterX}px`;
    waterAura.style.top = `${attackerCenterY}px`;
    container.appendChild(waterAura);
    elements.push(waterAura);
    
    // Create water preparation particles around attacker
    for (let i = 0; i < 12; i++) {
      const prepTimer = setTimeout(() => {
        const particle = document.createElement('div');
        particle.className = 'aqua-jet-prep-particle';
        
        const particleAngle = (i / 12) * Math.PI * 2;
        const particleDistance = 25 + Math.random() * 15;
        particle.style.left = `${attackerCenterX + Math.cos(particleAngle) * particleDistance}px`;
        particle.style.top = `${attackerCenterY + Math.sin(particleAngle) * particleDistance}px`;
        particle.style.setProperty('--particle-delay', `${i * 0.02}s`);
        
        container.appendChild(particle);
        elements.push(particle);
        
        // Auto-remove particle
        const particleRemoveTimer = setTimeout(() => {
          if (particle.isConnected) {
            particle.remove();
          }
        }, 400);
        timers.push(particleRemoveTimer);
      }, i * 15);
      timers.push(prepTimer);
    }

    // PHASE 2: WATER PROJECTILE STREAM (200-500ms)
    const waterStreamTimer = setTimeout(() => {
      // Create multiple water projectiles like Bubble animation
      const totalProjectiles = 15;
      
      for (let i = 0; i < totalProjectiles; i++) {
        const projectileTimer = setTimeout(() => {
          const waterProjectile = document.createElement('div');
          waterProjectile.className = 'aqua-jet-water-projectile';
          waterProjectile.style.left = `${attackerCenterX}px`;
          waterProjectile.style.top = `${attackerCenterY}px`;
          
          // Water projectile properties (similar to bubble)
          const projectileSize = 8 + Math.random() * 12; // 8-20px for variety
          const travelTime = 400 + Math.random() * 200; // 400-600ms (faster than bubbles)
          const spread = 20; // Tighter spread for more focused attack
          
          // Calculate destination with slight spread
          const targetX = defenderCenterX + (Math.random() - 0.5) * spread;
          const targetY = defenderCenterY + (Math.random() - 0.5) * spread;
          
          waterProjectile.style.setProperty('--target-x', `${targetX - attackerCenterX}px`);
          waterProjectile.style.setProperty('--target-y', `${targetY - attackerCenterY}px`);
          waterProjectile.style.setProperty('--projectile-size', `${projectileSize}px`);
          waterProjectile.style.setProperty('--travel-time', `${travelTime}ms`);
          waterProjectile.style.setProperty('--projectile-rotation', `${(Math.random() - 0.5) * 90}deg`);
          
          container.appendChild(waterProjectile);
          elements.push(waterProjectile);
          
          // Water splash effect on arrival
          const splashTimer = setTimeout(() => {
            if (waterProjectile.isConnected) {
              // Create water splash effect
              const splash = document.createElement('div');
              splash.className = 'aqua-jet-projectile-splash';
              splash.style.left = `${targetX}px`;
              splash.style.top = `${targetY}px`;
              splash.style.setProperty('--splash-size', `${projectileSize * 1.8}px`);
              
              container.appendChild(splash);
              elements.push(splash);
              
              // Remove projectile and splash
              waterProjectile.remove();
              
              const splashRemoveTimer = setTimeout(() => {
                if (splash.isConnected) {
                  splash.remove();
                }
              }, 300);
              timers.push(splashRemoveTimer);
            }
          }, travelTime);
          timers.push(splashTimer);
        }, i * 30); // Staggered projectile release (faster than bubbles)
        timers.push(projectileTimer);
      }
    }, 200);
    timers.push(waterStreamTimer);
        
    // PHASE 3: IMPACT AND SPLASH (500-800ms)
    const impactTimer = setTimeout(() => {
      // Create massive water impact splash
      const impactSplash = document.createElement('div');
      impactSplash.className = 'aqua-jet-impact-splash';
      impactSplash.style.left = `${defenderCenterX}px`;
      impactSplash.style.top = `${defenderCenterY}px`;
      container.appendChild(impactSplash);
      elements.push(impactSplash);
      
      // Create water burst explosion
      const waterBurst = document.createElement('div');
      waterBurst.className = 'aqua-jet-water-burst';
      waterBurst.style.left = `${defenderCenterX}px`;
      waterBurst.style.top = `${defenderCenterY}px`;
      container.appendChild(waterBurst);
      elements.push(waterBurst);
      
      // Make defender react with impact animation
      defenderEl.classList.add('aqua-jet-hit');
      defenderEl.style.filter = 'brightness(1.3) hue-rotate(200deg) saturate(1.5)';
      defenderEl.style.transform = 'translateX(15px) scale(0.95)';
      
      // Create water droplet particles on impact
      for (let i = 0; i < 20; i++) {
        const droplet = document.createElement('div');
        droplet.className = 'aqua-jet-water-droplet';
        
        const dropletAngle = Math.random() * Math.PI * 2;
        const dropletDistance = 20 + Math.random() * 40;
        droplet.style.left = `${defenderCenterX + Math.cos(dropletAngle) * dropletDistance}px`;
        droplet.style.top = `${defenderCenterY + Math.sin(dropletAngle) * dropletDistance}px`;
        
        droplet.style.setProperty('--droplet-velocity-x', `${(Math.random() - 0.5) * 60}px`);
        droplet.style.setProperty('--droplet-velocity-y', `${-20 - Math.random() * 30}px`);
        droplet.style.setProperty('--droplet-size', `${3 + Math.random() * 4}px`);
        
        container.appendChild(droplet);
        elements.push(droplet);
        
        const dropletTimer = setTimeout(() => {
          if (droplet.parentNode) {
            droplet.remove();
          }
        }, 600 + Math.random() * 200);
        timers.push(dropletTimer);
      }
      
      // Create impact shockwave rings
      for (let i = 0; i < 3; i++) {
        const shockwaveTimer = setTimeout(() => {
          const shockwave = document.createElement('div');
          shockwave.className = 'aqua-jet-shockwave';
          shockwave.style.left = `${defenderCenterX}px`;
          shockwave.style.top = `${defenderCenterY}px`;
          shockwave.style.setProperty('--shockwave-delay', `${i * 0.1}s`);
          shockwave.style.setProperty('--shockwave-size', `${80 + i * 40}px`);
          
          container.appendChild(shockwave);
          elements.push(shockwave);
          
          const shockwaveRemoveTimer = setTimeout(() => {
            if (shockwave.isConnected) {
              shockwave.remove();
            }
          }, 500);
          timers.push(shockwaveRemoveTimer);
        }, i * 50);
        timers.push(shockwaveTimer);
      }
      
      // Reset defender after hit effect
      const defenderResetTimer = setTimeout(() => {
        defenderEl.classList.remove('aqua-jet-hit');
        defenderEl.style.filter = '';
        defenderEl.style.transform = '';
      }, 400);
      timers.push(defenderResetTimer);
      
    }, 500); // Impact timing
    timers.push(impactTimer);
    
    // PHASE 4: RESET ATTACKER (800-1100ms)
    const resetTimer = setTimeout(() => {
      // Reset attacker to normal state (no movement needed)
      attackerEl.style.transition = 'transform 0.3s ease-out, filter 0.3s ease-out';
      attackerEl.style.transform = '';
      attackerEl.style.filter = '';
    }, 800);
    timers.push(resetTimer);

    // Complete animation and cleanup
    const completeTimer = setTimeout(() => {
      onComplete();
    }, 1100);
    timers.push(completeTimer);
    
    // Cleanup function
    return () => {
      // Clear all timers
      timers.forEach(timer => clearTimeout(timer));
      
      // Reset attacker
      if (attackerEl) {
        attackerEl.style.transition = '';
        attackerEl.style.transform = '';
        attackerEl.style.filter = '';
      }
      
      // Reset defender
      if (defenderEl) {
        defenderEl.classList.remove('aqua-jet-hit');
        defenderEl.style.filter = '';
        defenderEl.style.transform = '';
      }
      
      // Remove all created elements
      elements.forEach(element => {
        if (element && element.parentNode) {
          element.remove();
        }
      });
      
      // Remove any remaining effects
      const remainingElements = container.querySelectorAll(
        '.aqua-jet-water-aura, .aqua-jet-prep-particle, .aqua-jet-water-projectile, ' +
        '.aqua-jet-projectile-splash, .aqua-jet-impact-splash, ' +
        '.aqua-jet-water-burst, .aqua-jet-water-droplet, .aqua-jet-shockwave'
      );
      remainingElements.forEach(el => el.remove());
    };
  }, [playerRef, opponentRef, onComplete]);
  
  return null;
}

AquaJetAnimation.moveType = "water";
